import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { useDispatch } from "react-redux";
import { createCategory } from "../../redux/slices/categoriesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { compressImageToBase64, validateImageFile, getFileSizeInMB } from "../../utils/imageUtils";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { useTranslation } from "../../contexts/LanguageContext";

function NewCategoryModal({ isOpen, setIsOpen, categories }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    localisedKey: "",
    coverPhotoBase64: null,
    parentId: "",
    priorityOrder: "",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        dispatch(showNotification({ message: validation.error, type: "error" }));
        return;
      }

      setIsCompressing(true);
      try {
        // Show original file size
        const originalSize = getFileSizeInMB(file);
        console.log(`Original image size: ${originalSize}MB`);

        // Compress image to base64
        const compressedBase64 = await compressImageToBase64(file, 800, 600, 0.8);
        
        // Calculate compressed size (approximate)
        const compressedSize = ((compressedBase64.length * 3) / 4 / (1024 * 1024)).toFixed(2);
        console.log(`Compressed image size: ${compressedSize}MB`);

        setFormData((prev) => ({ ...prev, coverPhotoBase64: compressedBase64 }));
        setImagePreview(compressedBase64);
        
        dispatch(showNotification({
          message: t("categoryForm.compressedFromTo").replace("{from}", originalSize).replace("{to}", compressedSize),
          type: "success"
        }));
      } catch (error) {
        console.error('Error compressing image:', error);
        dispatch(showNotification({ message: t("categoryForm.errorProcessing"), type: "error" }));
      } finally {
        setIsCompressing(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      categoryName: formData.name,
      localisedKey: formData.localisedKey || "",
      priorityOrder: Number(formData.priorityOrder),
      parentId: formData.parentId || null,
      categoryImage: formData.coverPhotoBase64 || "", // Use real base64 or empty string
    };

    dispatch(createCategory(payload))
      .unwrap()
      .then(() => {
        dispatch(showNotification({ message: t("categoryForm.createdSuccess"), type: "success" }));
        setFormData({ name: "", localisedKey: "", coverPhotoBase64: null, parentId: "", priorityOrder: "" });
        setImagePreview(null);
        setIsOpen(false);
      })
      .catch(() => {
        dispatch(showNotification({ message: t("categoryForm.failedCreate"), type: "error" }));
      });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="max-w-md w-full space-y-4 bg-white rounded-lg shadow-md p-6">
              <Dialog.Title className="text-lg font-medium">{t("categoryForm.addTitle")}</Dialog.Title>
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-900">
                      {t("categoryForm.name")}
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-themeOrange focus:outline-none sm:text-sm"
                      required
                    />
                  </div>

                  {/* Localised Key Field */}
                  <div>
                    <label htmlFor="localisedKey" className="block text-sm font-medium text-gray-900">
                      {t("categoryForm.localisedKey")}
                    </label>
                    <input
                      id="localisedKey"
                      name="localisedKey"
                      type="text"
                      value={formData.localisedKey}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-themeOrange focus:outline-none sm:text-sm"
                      placeholder={t("categoryForm.localisedKeyPlaceholder")}
                    />
                  </div>

                  {/* Cover Photo Field */}
                  <div>
                    <label htmlFor="cover-photo" className="block text-sm font-medium text-gray-900">
                      {t("categoryForm.coverPhoto")}
                    </label>
                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10 relative">
                      <input
                        id="file-upload"
                        name="coverPhoto"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        disabled={isCompressing}
                      />
                      {isCompressing ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-themeOrange"></div>
                          <p className="mt-2 text-sm text-gray-600">{t("categoryForm.compressing")}</p>
                        </div>
                      ) : imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="mx-auto h-24 w-24 object-cover rounded-md" />
                      ) : (
                        <div className="text-center">
                          <img src={placeholderImage} alt="Placeholder" className="mx-auto h-24 w-24 object-cover rounded-md mb-2 opacity-50" />
                          <PhotoIcon aria-hidden="true" className="mx-auto h-12 w-12 text-gray-300" />
                          <div className="mt-4 text-sm leading-6 text-gray-600">{t("categoryForm.uploadOrDefault")}</div>
                          <p className="text-xs leading-5 text-gray-600">{t("categoryForm.fileHelp")}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Priority Field */}
                  <div>
                    <label htmlFor="priorityOrder" className="block text-sm font-medium text-gray-900">
                      {t("categoryForm.priority")}
                    </label>
                    <input
                      id="priorityOrder"
                      name="priorityOrder"
                      type="number"
                      value={formData.priorityOrder}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-themeOrange focus:outline-none sm:text-sm"
                      placeholder={t("categoryForm.priorityPlaceholder")}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">{t("categoryForm.priorityHelp")}</p>
                  </div>

                  {/* Parent Category Field */}
                  <div>
                    <label htmlFor="parentId" className="block text-sm font-medium text-gray-900">
                      {t("categoryForm.parentCategory")}
                    </label>
                    <select
                      id="parentId"
                      name="parentId"
                      value={formData.parentId}
                      onChange={handleInputChange}
                      className="block w-full mt-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-themeOrange focus:outline-none sm:text-sm"
                    >
                      <option value="">{t("categoryForm.none")}</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6">
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-700"
                    onClick={() => setIsOpen(false)}
                    disabled={isCompressing}
                  >
                    {t("categoryForm.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="text-sm font-medium text-white bg-themeOrange rounded-md px-4 py-1 disabled:opacity-50"
                    disabled={isCompressing}
                  >
                    {t("categoryForm.create")}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default NewCategoryModal;
